import React, { useMemo } from 'react';
import { Paper, Group, Stack, Text, Title, Badge, Loader, SimpleGrid, Divider } from '@mantine/core';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import config from '../../../config';

const money = (v) =>
  v == null ? '—' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num  = (v) => (v == null ? '—' : Number(v).toLocaleString('en-US'));
const days = (v) => (v == null ? '—' : `${Number(v).toLocaleString('en-US')}d`);
const date = (v) => (v == null ? '—' : new Date(v).toLocaleDateString('en-US'));
const txt  = (v) => { const s = v == null ? '' : String(v).trim(); return s || '—'; };

// Plain-English reason, derived purely from the serialized raw fields (mirrors the three
// pricing paths in VIEW_ROTABULL_SERIALIZED_PRICING_RAW).
function buildReason(r) {
  if (!r) return 'No serialized pricing detail found for this stock line.';
  const cond    = txt(r.condition);
  const bracket = txt(r.dos_bracket);
  const cls     = txt(r.part_class);
  const hasMin  = r.minimum_price != null && Number(r.minimum_price) > 0;
  const price   = r.rotabull_price;

  const age = r.ssp_age_days != null ? `, priced ${num(r.ssp_age_days)}d ago` : '';

  if (hasMin) {
    return `Priced at the serial-number minimum of ${money(r.minimum_price)} — this overrides DOS discounting.`;
  }
  if (price != null) {
    const disc  = r.discount_pct != null ? Number(r.discount_pct) : 0;
    const shelf = `On shelf ${num(r.dos)} days (${bracket})`;
    return disc > 0
      ? `SSP ${money(r.ssp)} (${cond}${age}). ${shelf} → ${disc}% DOS discount → ${money(price)}.`
      : `SSP ${money(r.ssp)} (${cond}${age}). ${shelf} → no DOS discount → ${money(price)}.`;
  }
  // No price (path 3): expired SSP or none
  if (txt(r.is_ssp_expired) === 'Y') {
    return `No automated price: SSP is expired (Class ${cls}${age}).`;
  }
  return `No automated price: no SSP set for condition ${cond}.`;
}

function Field({ label, children }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="sm">{children}</Text>
    </Stack>
  );
}

export default function PriceExplainPanel({ selected }) {
  const isSerialized = selected != null && selected.stock_line != null;

  const url = useMemo(() => {
    if (!isSerialized) return null;
    const p = new URLSearchParams({
      partNumber: String(selected.part_number ?? '').trim(),
      condition:  String(selected.condition   ?? '').trim(),
      stockLine:  String(selected.stock_line),
    });
    return `${config.apiBaseUrl}/api/rotabull/price-lines/explain?${p}`;
  }, [isSerialized, selected]);

  const { data, isLoading } = useAuthenticatedData(url);

  if (!selected) {
    return (
      <Paper withBorder p="md" radius="md">
        <Text size="sm" c="dimmed">Select a row to see why its price is what it is.</Text>
      </Paper>
    );
  }

  // Part-level (no stock line) — simple note, no DOS discount applies.
  if (!isSerialized) {
    return (
      <Paper withBorder p="md" radius="md">
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Group gap="xs">
              <Title order={5}>{txt(selected.part_number)} · {txt(selected.condition)}</Title>
              <Badge color="gray" variant="light">Part-level</Badge>
            </Group>
            <Text size="sm">
              Part-level price sent to all customers. No inventory-level DOS discount applies
              (this row isn’t tied to a specific stock line).
            </Text>
          </Stack>
          <Stack gap={2} align="flex-end">
            <Text size="xs" c="dimmed">Feed price</Text>
            <Title order={4}>{money(selected.price_in_usd)}</Title>
          </Stack>
        </Group>
        <Divider my="sm" />
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
          <Field label="Category">{txt(selected.company_category)}</Field>
          <Field label="Trace To">{txt(selected.trace_to)}</Field>
          <Field label="Tag Source">{txt(selected.tag_source)}</Field>
          <Field label="Expires">{date(selected.expiration_date)}</Field>
        </SimpleGrid>
      </Paper>
    );
  }

  const r = data?.row;
  const reason = buildReason(r);

  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Group gap="xs">
            <Title order={5}>{txt(selected.part_number)} · {txt(selected.condition)}</Title>
            <Badge color="blue" variant="light">Serialized</Badge>
            {selected.serial_number && <Badge color="gray" variant="outline">S/N {txt(selected.serial_number)}</Badge>}
            <Badge color="dark" variant="outline">Stock line {num(selected.stock_line)}</Badge>
          </Group>
          {isLoading
            ? <Group gap="xs"><Loader size="xs" /><Text size="sm" c="dimmed">Loading detail…</Text></Group>
            : <Text size="sm">{reason}</Text>}
        </Stack>
        <Stack gap={2} align="flex-end">
          <Text size="xs" c="dimmed">Rotabull price</Text>
          <Title order={4}>{money(r ? r.rotabull_price : selected.price_in_usd)}</Title>
        </Stack>
      </Group>

      {!isLoading && r && (
        <>
          <Divider my="sm" label="Why" labelPosition="left" />
          <SimpleGrid cols={{ base: 2, sm: 4, lg: 6 }} spacing="md">
            <Field label="DOS (days on shelf)">{num(r.dos)} <Text span size="xs" c="dimmed">({txt(r.dos_bracket)})</Text></Field>
            <Field label="Repair / tag age">{days(r.tag_age_days)}</Field>
            <Field label="Repaired">{date(r.repaired_date)}</Field>
            <Field label="Received">{date(r.received_date)}</Field>
            <Field label="Class">{txt(r.part_class)}</Field>
            <Field label="SSP">{money(r.ssp)}</Field>
            <Field label="SSP age">{days(r.ssp_age_days)}</Field>
            <Field label="SSP expired">
              {txt(r.is_ssp_expired) === 'Y'
                ? <Badge color="red" size="sm" variant="filled">Yes</Badge>
                : <Badge color="green" size="sm" variant="filled">No</Badge>}
            </Field>
            <Field label="Min price">{r.minimum_price != null && Number(r.minimum_price) > 0 ? money(r.minimum_price) : '—'}</Field>
            <Field label="Discount %">{r.discount_pct == null ? '—' : `${r.discount_pct}%`}</Field>
            <Field label="Discount $">{money(r.discount_dollar)}</Field>
            <Field label="Final price">{money(r.rotabull_price)}</Field>
            <Field label="Trace">{txt(r.trace_to)} <Text span size="xs" c="dimmed">{txt(r.trace_type)}</Text></Field>
            <Field label="WH / Lot">{txt(r.wh)} / {txt(r.lot_wh)}</Field>
          </SimpleGrid>
        </>
      )}

      {!isLoading && !r && data && (
        <>
          <Divider my="sm" />
          <Text size="sm" c="dimmed">No serialized pricing detail found for this stock line.</Text>
        </>
      )}
    </Paper>
  );
}
